import React, { useMemo, useContext } from 'react';

import CardSlider from 'components/CardSlider';
import { useListingSliderQuery } from 'hooks/useListingSliderQuery';
import useLiveData from 'hooks/useLiveData';
import { PersonalizationContext } from 'components/Personalization/PersonalizationContext';

const ListingSliderProvider = ({ children, staticData, id, ...props }) => {
	const { matchAudience } = useContext(PersonalizationContext);
	const { data } = useListingSliderQuery();
	const shouldLimitLiveDataByStaticIds = !staticData;
	const effectiveStaticData = React.useMemo(() => staticData || data || {}, [staticData, data]);
	const listingEdges = effectiveStaticData?.allSimpleviewListing?.edges || [];
	const listingNodes = listingEdges.map(({ node }) => node);
	const sliderEdges = effectiveStaticData?.allSmartTagsListingSlider?.edges || [];
	const sliderNodes = sliderEdges.map(({ node }) => node);

	const {
		statusText: sliderStatusText,
		response: sliderResponse,
		visibilityChange: sliderVisibilityChange,
	} = useLiveData({
		namespace: 'SmartTags',
		model: 'ListingSlider',
		method: 'get',
		graphqlKey: 'allSmartTagsListingSlider',
		apiParams: {
			archived: false,
			approved: true,
			deleted: false,
			...(shouldLimitLiveDataByStaticIds && sliderNodes.length > 0 ? {
				id: sliderNodes.map((n) => n.id).join(','),
			} : {}),
			...(!!id ? { id } : {}),
		},
		staticData: effectiveStaticData,
	});

	const liveSlider = sliderResponse?.data[0];
	let s = sliderNodes[0];
	if (!!liveSlider && sliderStatusText === 'LOADED') {
		s = liveSlider;
	}
	if (!!liveSlider && sliderVisibilityChange) s = liveSlider;

	const sliderDatum = useMemo(() => s, [s]);

	const {
		statusText: listingStatusText,
		response: listingResponse,
		visibilityChange: listingVisibilityChange,
	} = useLiveData({
		namespace: 'Simpleview',
		model: 'Listing',
		method: 'all',
		graphqlKey: 'allSimpleviewListing',
		apiParams: {
			archived: false,
			approved: true,
			deleted: false,
		},
		staticData: effectiveStaticData,
	});

	const liveListings = listingResponse?.data;
	let l = listingNodes;
	if (!!liveListings && listingStatusText === 'LOADED') {
		l = liveListings;
	}
	if (!!liveListings && listingVisibilityChange) l = liveListings;

	const listingsData = useMemo(() => l, [l]);

	if (!!sliderDatum && sliderDatum.hasOwnProperty('Audience') && sliderDatum?.Audience) {
		if (!matchAudience(sliderDatum?.Audience)) {
			return null;
		}
	}

	const filteredListings = listingsData?.filter(({ id, Category }) => {
			const sharedCategory = Category?.some(({ object }) => sliderDatum?.Category?.some(({ object: postObject }) => postObject.id === object.id));
			return sharedCategory;
		})?.map(({ companyName, photo, uri, description }) => ({ title: companyName, image: photo, href: uri, description: description.length > 95 ? description.slice(0, 95) + '...' : description, type: 'blog' }));

	return (
		<CardSlider 
			{...sliderDatum} 
			cta={{uri: sliderDatum?.link, linkText: sliderDatum?.linkText}} 
			slider={{ slides: filteredListings }} 
			layout={props.layout ?? 'fullwidth'} 
			flush={true} 
			matchAudience={matchAudience} 
		/>
	);
};

export default ListingSliderProvider;